import React, { useEffect, useState } from 'react';
import { Box, Chip, FormControl, MenuItem, Select, SelectChangeEvent, InputLabel, OutlinedInput, Checkbox, ListItemText, TextField, InputAdornment, Tooltip, IconButton } from '@mui/material';
import { Icon } from '@iconify/react';
import { InfoIcon } from 'lucide-react';
import DataTable, { Column, RowAction, FilterChip } from './DataTable';
import { shouldShowResendEmail } from '../utils/passwordResetHistory';
interface SubAgentUser {
  id: number;
  name: string;
  fatherName: string;
  cnic: string;
  email: string;
  phone: string;
  username: string;
  branchCode: string;
  settlementAccount: string;
  subAgent: string;
  status: 'active' | 'inactive';
  userType: 'maker' | 'checker';
  createdAt: string;
}
interface SubAgentUsersTableProps {
  users: SubAgentUser[];
  onEditUser: (user: SubAgentUser) => void;
  searchQuery: string;
  subAgentFilter: string | null;
  onStatusChange?: (user: SubAgentUser, newStatus: 'active' | 'inactive') => void;
  onResetPassword?: (user: SubAgentUser) => void;
  onViewResetHistory?: (user: SubAgentUser) => void;
  onResendEmail?: (user: SubAgentUser) => void;
}
interface SubAgentRecord {
  id: number;
  name: string;
  fatherName: string;
  cnic: string;
  email: string;
  phone: string;
  username: string;
  branchCode: string;
  settlementAccount: string;
  subAgent: string;
  status: 'active' | 'inactive';
  userType: 'maker' | 'checker';
  createdAt: string;
}
interface SubAgentRecordsTableProps {
  records: SubAgentRecord[];
  onEditRecord: (record: SubAgentRecord) => void;
  searchQuery: string;
  subAgentFilter: string | null;
  onStatusChange?: (record: SubAgentRecord, newStatus: 'active' | 'inactive') => void;
  onResetPassword?: (record: SubAgentRecord) => void;
  onViewResetHistory?: (record: SubAgentRecord) => void;
  onResendEmail?: (record: SubAgentRecord) => void;
}
const SUB_AGENTS = ['Islamabad Exchange', 'Karachi Money Transfer', 'Lahore Financial', 'Peshawar Remit', 'Quetta Exchange'];
const BRANCH_CODES = ['ISB001', 'ISB002', 'KHI001', 'KHI002', 'LHR001', 'LHR002', 'PSH001', 'QTA001'];
const SubAgentRecordsTable: React.FC<SubAgentRecordsTableProps> = ({
  records,
  onEditRecord,
  searchQuery,
  subAgentFilter,
  onStatusChange,
  onResetPassword,
  onViewResetHistory,
  onResendEmail
}) => {
  const [filters, setFilters] = useState({
    subAgents: subAgentFilter ? [subAgentFilter] : [] as string[],
    branchCodes: [] as string[],
    status: 'all',
    userType: 'all',
    dateFrom: '',
    dateTo: '',
    search: searchQuery || '',
    name: '',
    fatherName: '',
    cnic: '',
    email: '',
    phone: '',
    username: '',
    settlementAccount: ''
  });
  const [loading, setLoading] = useState(false);
  const [columnFilters, setColumnFilters] = useState<{
    [key: string]: string | string[];
  }>({});
  const validateDateRange = (from: string, to: string): boolean => {
    if (!from || !to) return true;
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const diffMonths = (toDate.getFullYear() - fromDate.getFullYear()) * 12 + (toDate.getMonth() - fromDate.getMonth());
    return diffMonths <= 3;
  };
  const handleDateChange = (field: 'dateFrom' | 'dateTo', value: string) => {
    const newFilters = {
      ...filters,
      [field]: value
    };
    if (newFilters.dateFrom && newFilters.dateTo && !validateDateRange(newFilters.dateFrom, newFilters.dateTo)) {
      return;
    }
    setFilters(newFilters);
  };
  // Handle column filter change
  const handleColumnFilterChange = (columnId: string, value: string | string[]) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnId]: value
    }));
  };
  // Handle text filter change
  const handleTextFilterChange = (field: string, value: string) => {
    setFilters({
      ...filters,
      [field]: value
    });
  };
  // Update search when searchQuery prop changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      search: searchQuery || ''
    }));
  }, [searchQuery]);
  // Apply filters to the data
  const filteredRecords = records.filter(record => {
    // Apply search query
    if (filters.search) {
      const query = filters.search.toLowerCase();
      if (!record.name.toLowerCase().includes(query) && !record.email.toLowerCase().includes(query) && !record.cnic.toLowerCase().includes(query) && !record.phone.toLowerCase().includes(query) && !record.username.toLowerCase().includes(query) && !record.subAgent.toLowerCase().includes(query) && !record.branchCode.toLowerCase().includes(query)) {
        return false;
      }
    }
    // Column-specific text filters
    if (filters.name && !record.name.toLowerCase().includes(filters.name.toLowerCase())) {
      return false;
    }
    if (filters.fatherName && !record.fatherName.toLowerCase().includes(filters.fatherName.toLowerCase())) {
      return false;
    }
    if (filters.cnic && !record.cnic.toLowerCase().includes(filters.cnic.toLowerCase())) {
      return false;
    }
    if (filters.email && !record.email.toLowerCase().includes(filters.email.toLowerCase())) {
      return false;
    }
    if (filters.phone && !record.phone.toLowerCase().includes(filters.phone.toLowerCase())) {
      return false;
    }
    if (filters.username && !record.username.toLowerCase().includes(filters.username.toLowerCase())) {
      return false;
    }
    if (filters.settlementAccount && !record.settlementAccount.toLowerCase().includes(filters.settlementAccount.toLowerCase())) {
      return false;
    }
    // Sub Agent filter
    if (filters.subAgents.length > 0 && !filters.subAgents.includes(record.subAgent)) {
      return false;
    }
    // Branch code filter
    if (filters.branchCodes.length > 0 && !filters.branchCodes.includes(record.branchCode)) {
      return false;
    }
    // Status filter
    if (filters.status !== 'all' && record.status !== filters.status) {
      return false;
    }
    // User type filter from main filters
    if (filters.userType !== 'all' && record.userType !== filters.userType) {
      return false;
    }
    // User type filter from column filter
    if (columnFilters.userType && columnFilters.userType !== 'all' && record.userType !== columnFilters.userType) {
      return false;
    }
    // Status filter from column filter
    if (columnFilters.status && columnFilters.status !== 'all' && record.status !== columnFilters.status) {
      return false;
    }
    // SubAgent filter from column filter
    if (columnFilters.subAgent && columnFilters.subAgent !== 'all' && record.subAgent !== columnFilters.subAgent) {
      return false;
    }
    // Branch code filter from column filter
    if (columnFilters.branchCode && columnFilters.branchCode !== 'all' && record.branchCode !== columnFilters.branchCode) {
      return false;
    }
    // Date filters
    if (filters.dateFrom) {
      const userDate = new Date(record.createdAt);
      const fromDate = new Date(filters.dateFrom);
      if (userDate < fromDate) {
        return false;
      }
    }
    if (filters.dateTo) {
      const userDate = new Date(record.createdAt);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (userDate > toDate) {
        return false;
      }
    }
    return true;
  });
  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      subAgents: [],
      branchCodes: [],
      status: 'all',
      userType: 'all',
      dateFrom: '',
      dateTo: '',
      search: '',
      name: '',
      fatherName: '',
      cnic: '',
      email: '',
      phone: '',
      username: '',
      settlementAccount: ''
    });
    setColumnFilters({});
  };
  // Handle search change
  const handleSearchChange = (value: string) => {
    setFilters({
      ...filters,
      search: value
    });
  };
  // Define filter chips
  const filterChips: FilterChip[] = [...(filters.name ? [{
    id: 'name',
    label: 'Name',
    value: filters.name,
    onDelete: () => handleTextFilterChange('name', '')
  }] : []), ...(filters.fatherName ? [{
    id: 'fatherName',
    label: 'Father Name',
    value: filters.fatherName,
    onDelete: () => handleTextFilterChange('fatherName', '')
  }] : []), ...(filters.cnic ? [{
    id: 'cnic',
    label: 'CNIC',
    value: filters.cnic,
    onDelete: () => handleTextFilterChange('cnic', '')
  }] : []), ...(filters.email ? [{
    id: 'email',
    label: 'Email',
    value: filters.email,
    onDelete: () => handleTextFilterChange('email', '')
  }] : []), ...(filters.phone ? [{
    id: 'phone',
    label: 'Phone',
    value: filters.phone,
    onDelete: () => handleTextFilterChange('phone', '')
  }] : []), ...(filters.username ? [{
    id: 'username',
    label: 'Username',
    value: filters.username,
    onDelete: () => handleTextFilterChange('username', '')
  }] : []), ...(filters.settlementAccount ? [{
    id: 'settlementAccount',
    label: 'Settlement Account',
    value: filters.settlementAccount,
    onDelete: () => handleTextFilterChange('settlementAccount', '')
  }] : []), ...filters.subAgents.map(subAgent => ({
    id: `subAgent-${subAgent}`,
    label: 'Sub Agent',
    value: subAgent,
    onDelete: () => setFilters({
      ...filters,
      subAgents: filters.subAgents.filter(s => s !== subAgent)
    })
  })), ...filters.branchCodes.map(code => ({
    id: `branchCode-${code}`,
    label: 'Branch',
    value: code,
    onDelete: () => setFilters({
      ...filters,
      branchCodes: filters.branchCodes.filter(c => c !== code)
    })
  })), ...(filters.status !== 'all' ? [{
    id: 'status',
    label: 'Status',
    value: filters.status,
    onDelete: () => setFilters({
      ...filters,
      status: 'all'
    })
  }] : []), ...(filters.userType !== 'all' ? [{
    id: 'userType',
    label: 'User Type',
    value: filters.userType === 'maker' ? 'Maker' : 'Checker',
    onDelete: () => setFilters({
      ...filters,
      userType: 'all'
    })
  }] : []), ...(filters.dateFrom ? [{
    id: 'dateFrom',
    label: 'From',
    value: new Date(filters.dateFrom).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateFrom: ''
    })
  }] : []), ...(filters.dateTo ? [{
    id: 'dateTo',
    label: 'To',
    value: new Date(filters.dateTo).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateTo: ''
    })
  }] : []), ...Object.entries(columnFilters).filter(([key, value]) => value !== 'all' && value !== '').map(([key, value]) => ({
    id: `column-${key}-${value}`,
    label: columns.find(col => col.id === key)?.label || key,
    value: typeof value === 'string' ? value : value.join(', '),
    onDelete: () => handleColumnFilterChange(key, ['status', 'userType', 'subAgent', 'branchCode'].includes(key) ? 'all' : '')
  }))];
  // Define columns
  const columns: Column<SubAgentRecord>[] = [{
    id: 'name',
    label: 'Name',
    renderFilter: () => <TextField size="small" placeholder="Search name" value={filters.name} onChange={e => handleTextFilterChange('name', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-2304" data-mp="obae5zkvbvm8">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-2305" data-mp="o8eiyj0enl1h" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-2303" data-mp="3p60c244sb4pz" />
  }, {
    id: 'fatherName',
    label: 'Father Name',
    renderFilter: () => <TextField size="small" placeholder="Search father name" value={filters.fatherName} onChange={e => handleTextFilterChange('fatherName', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-2307" data-mp="3iqrttok9l6ru">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-2308" data-mp="3is2x7n9sscif" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-2306" data-mp="123qntj89yrw4" />
  }, {
    id: 'cnic',
    label: 'CNIC',
    renderFilter: () => <TextField size="small" placeholder="Search CNIC" value={filters.cnic} onChange={e => handleTextFilterChange('cnic', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-2310" data-mp="23q111p78txow">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-2311" data-mp="2ih9y2jdh6lc2" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-2309" data-mp="2nfbjhaydiwqp" />
  }, {
    id: 'email',
    label: 'Email',
    renderFilter: () => <TextField size="small" placeholder="Search email" value={filters.email} onChange={e => handleTextFilterChange('email', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-2313" data-mp="3rp3jcfcokicd">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-2314" data-mp="3rnsf6efbp8kq" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-2312" data-mp="1l1w1yv1j70wf" />
  }, {
    id: 'phone',
    label: 'Phone',
    renderFilter: () => <TextField size="small" placeholder="Search phone" value={filters.phone} onChange={e => handleTextFilterChange('phone', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-2316" data-mp="15kbbkwa9890g">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-2317" data-mp="10kf9x3p14to2" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-2315" data-mp="a8vfywpqljpp" />
  }, {
    id: 'username',
    label: 'Username',
    renderFilter: () => <TextField size="small" placeholder="Search username" value={filters.username} onChange={e => handleTextFilterChange('username', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-2319" data-mp="277atbaijwqed">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-2320" data-mp="236ijaf1uuhh1" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-2318" data-mp="ms93waflc32c" />
  }, {
    id: 'branchCode',
    label: 'Branch Code',
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-2321" data-mp="3utnms7246lml">
          <Select value={columnFilters.branchCode || 'all'} onChange={e => handleColumnFilterChange('branchCode', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-2322" data-mp="3uugrz3lfd32m">
            <MenuItem value="all" data-id="element-2323" data-mp="3b56fdygz6y8q">All Branches</MenuItem>
            {BRANCH_CODES.map(code => <MenuItem key={code} value={code} data-id="element-2324" data-mp="3g5am4ws30elr">
                {code}
              </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'settlementAccount',
    label: 'Settlement Account',
    renderFilter: () => <TextField size="small" placeholder="Search account" value={filters.settlementAccount} onChange={e => handleTextFilterChange('settlementAccount', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-2326" data-mp="pqwmzopeadn6">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-2327" data-mp="ktmycb9ty60e" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-2325" data-mp="vgd41xwpowkx" />
  }, {
    id: 'subAgent',
    label: 'Sub Agent',
    renderCell: row => <Chip label={row.subAgent} size="small" sx={{
      backgroundColor: '#E0F7FF',
      color: '#0EA5E9',
      height: 24
    }} data-id="element-2328" data-mp="30kj02wz5s2tg" />,
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-2329" data-mp="2mg1w1r8s2w5e">
          <Select value={columnFilters.subAgent || 'all'} onChange={e => handleColumnFilterChange('subAgent', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-2330" data-mp="2qgcbrvmdh0xg">
            <MenuItem value="all" data-id="element-2331" data-mp="22q88xqh0d57i">All Sub Agents</MenuItem>
            {SUB_AGENTS.map(agent => <MenuItem key={agent} value={agent} data-id="element-2332" data-mp="27qvwu39xx5xi">
                {agent}
              </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'userType',
    label: 'User Type',
    renderCell: row => <Chip label={row.userType === 'maker' ? 'Maker' : 'Checker'} size="small" sx={{
      backgroundColor: row.userType === 'maker' ? 'rgba(6, 182, 212, 0.1)' : 'rgba(139, 92, 246, 0.1)',
      color: row.userType === 'maker' ? '#06B6D4' : '#8B5CF6',
      height: 24,
      fontWeight: 600
    }} data-id="element-2333" data-mp="3o4clggdn55fp" />,
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-2334" data-mp="qlmkq9f1drjo">
          <Select value={columnFilters.userType || 'all'} onChange={e => handleColumnFilterChange('userType', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-2335" data-mp="viczmtf3uh3i">
            <MenuItem value="all" data-id="element-2336" data-mp="1a7rak5ybx6l0">All Types</MenuItem>
            <MenuItem value="maker" data-id="element-2337" data-mp="1e8uouo0qauk4">Maker</MenuItem>
            <MenuItem value="checker" data-id="element-2338" data-mp="1ebp9ognnmhe2">Checker</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => <Chip label={row.status === 'active' ? 'Active' : 'Inactive'} size="small" sx={{
      backgroundColor: row.status === 'active' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
      color: row.status === 'active' ? '#10B981' : '#EF4444',
      height: 24
    }} data-id="element-2339" data-mp="2vs7vrupw2zf" />,
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-2340" data-mp="38pueld8o6imr">
          <Select value={columnFilters.status || 'all'} onChange={e => handleColumnFilterChange('status', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-2341" data-mp="38qchgspu7v3n">
            <MenuItem value="all" data-id="element-2342" data-mp="3dnmeoujgxb21">All Status</MenuItem>
            <MenuItem value="active" data-id="element-2343" data-mp="2pyjouzeokbwg">Active</MenuItem>
            <MenuItem value="inactive" data-id="element-2344" data-mp="2px8prcw6jsbo">Inactive</MenuItem>
          </Select>
        </FormControl>
  }];
  // Define row actions
  const rowActions: RowAction<SubAgentRecord>[] = [{
    icon: 'lucide:eye',
    label: 'View',
    onClick: record => console.log('View record', record)
  }, {
    icon: 'lucide:edit',
    label: 'Edit',
    onClick: onEditRecord
  }, {
    icon: 'lucide:mail',
    label: 'Resend Email',
    onClick: record => onResendEmail?.(record),
    condition: record => shouldShowResendEmail(record)
  }, {
    icon: 'lucide:x-circle',
    label: 'Deactivate',
    onClick: record => onStatusChange?.(record, 'inactive'),
    condition: record => record.status === 'active'
  }, {
    icon: 'lucide:check-circle',
    label: 'Activate',
    onClick: record => onStatusChange?.(record, 'active'),
    condition: record => record.status === 'inactive'
  }, {
    icon: 'lucide:key',
    label: 'Reset Password',
    onClick: record => onResetPassword?.(record)
  }, {
    icon: 'lucide:clock',
    label: 'Password Reset History',
    onClick: record => onViewResetHistory?.(record)
  }, {
    icon: 'lucide:history',
    label: 'Audit Log',
    onClick: record => console.log('View audit trail for record', record)
  }];
  // Handle export
  const handleExport = () => {
    console.log('Export data');
  };
  return <DataTable title="Sub Agent Records" data={filteredRecords} columns={columns} rowActions={rowActions} keyField="id" filterChips={filterChips} onClearAllFilters={handleClearFilters} loading={loading} initialVisibleColumns={['name', 'email', 'phone', 'username', 'branchCode', 'subAgent', 'userType', 'status']} onSearchChange={handleSearchChange} searchPlaceholder="Search records by name, email, phone, branch code..." searchValue={filters.search} onExport={handleExport} emptyStateMessage="No records found" data-id="element-2345" data-mp="1qryd1mfqjsdv" />;
};
export default SubAgentRecordsTable;